import { dayjs } from '@/plugins/dayjs';

/**
 * Recursively parses an object (or array) and converts any string that appears
 * to be an ISO-formatted date into a Date object.
 *
 * @param value - The input value (object, array, or primitive).
 * @returns The transformed value with any date strings replaced with Date objects.
 */
export function deepParseDates(value: unknown): unknown {
  // Check if the value is a string that looks like an ISO date.
  if (typeof value === 'string') {
    // A simple (but not exhaustive) ISO 8601 regex.
    // You may adjust the regex if you have a very specific format.
    const isoDateRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
    if (isoDateRegex.test(value)) {
      const dt = new Date(value);
      // Check that the date is valid.
      if (!isNaN(dt.getTime())) {
        return dt;
      }
    }
    return value;
  }

  // If the value is an array, process each element.
  if (Array.isArray(value)) {
    return value.map(deepParseDates);
  }

  // If the value is an object, process each property.
  if (value && typeof value === 'object') {
    // Create a new object and process each key.
    // (Using Object.entries so that we preserve all enumerable properties.)
    const newObj: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      newObj[key] = deepParseDates(val);
    }
    return newObj;
  }

  // Return the value unchanged if it is a primitive other than string.
  return value;
}

export function formatDate(
  value: Date | string | undefined | null,
  defaultValue = '-'
): string {
  // check if value is null or undefined or empty string
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  // check if value is a string and convert to Date
  if (typeof value === 'string') {
    value = new Date(value);
  }
  // check if value is a Date object
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  // otherwise, return default value
  return defaultValue;
}

export function formatDateTime(
  value: Date | string | undefined | null,
  defaultValue = '-'
): string {
  // check if value is null or undefined or empty string
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  // check if value is a string and convert to Date
  if (typeof value === 'string') {
    value = new Date(value);
  }
  // check if value is a Date object
  if (value instanceof Date) {
    return value.toLocaleDateString() + ' ' + value.toLocaleTimeString();
  }
  // otherwise, return default value
  return defaultValue;
}

/**
 * Convert a Date to ISO format with time zone,
 * format "YYYY-MM-DDT00:00:00±HH:MM", e.g. "2025-08-20T00:00:00+02:00"
 */
export function formatDateTimeToIso(value: Date): string {
  const local = dayjs(value);
  return local.format('YYYY-MM-DD[T]HH:mm:ssZ');
}

/**
 * Convert a Date or "YYYY-MM-DD" string to ISO format for whole-day filtering.
 * - withTimeZone = false  -> "YYYY-MM-DD" (date-only, no offset)
 * - withTimeZone = true   -> "YYYY-MM-DDT00:00:00±HH:MM" (local midnight with offset)
 */
export function formatDateStringToIso(
  value: Date | string | undefined | null,
  withTimeZone = false
): string | null {
  // check if value is null or undefined or empty string
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // guess time zone
  const zone = dayjs.tz.guess();

  // normalize to a dayjs instance representing the same local date
  let day =
    typeof value === 'string'
      ? dayjs(value, 'YYYY-MM-DD', true) // strict parse of "YYYY-MM-DD"
      : dayjs(value);

  // If string parse failed (not "YYYY-MM-DD"), try generic parse as a fallback
  if (!day.isValid() && typeof value === 'string') {
    day = dayjs(value);
  }

  // If all parsing attempts failed, return null
  if (!day.isValid()) {
    return null;
  }

  // Move to local start-of-day and attach user's timezone (keeps wall time)
  const startOfDay = day.startOf('day').tz(zone, true);

  // Format
  return withTimeZone
    ? startOfDay.format('YYYY-MM-DD[T]HH:mm:ssZ') // e.g. "2025-08-20T00:00:00+02:00"
    : startOfDay.format('YYYY-MM-DD'); // e.g. "2025-08-20"
}

/**
 * Build an ISO8601 string with the user's local offset, e.g. "2025-08-20T15:00:00+02:00".
 * Expects date in "YYYY-MM-DD" and time in "HH:mm" (24h).
 */
export function formatDateTimeStringToIso(
  dateValue: string | undefined | null,
  timeValue: string | undefined | null,
  defaultTime = '00:00'
): string | null {
  if (typeof dateValue !== 'string' || !dateValue) {
    return null;
  }
  if (typeof timeValue !== 'string' || !timeValue) {
    timeValue = defaultTime;
  }

  // Parse as local wall time; strict format avoids ambiguous parsing.
  const local = dayjs(`${dateValue} ${timeValue}`, 'YYYY-MM-DD HH:mm', true);

  if (!local.isValid()) {
    return null; // also catches impossible DST times
  }

  // Format with explicit offset (keeps local zone; includes DST offset if applicable)
  return local.format('YYYY-MM-DD[T]HH:mm:ssZ');
}
